import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Share2, Volume2, VolumeX } from "lucide-react";
import { seanListPosts, mediaSrc } from "@/lib/api";

// Reels: every video Sean uploads. Row of 9:16 cards → full-screen vertical swipe player.
export default function SeanReels() {
  const [reels, setReels] = useState([]);
  const [openAt, setOpenAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => seanListPosts(50)
      .then((d) => { if (alive) setReels((d.items || []).filter((p) => p.upload_kind === "video")); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!reels.length) return null;

  return (
    <section id="sean-reels" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12" data-testid="sean-reels">
      <div className="mb-4">
        <div className="eyebrow text-[var(--red)]">🎬 lab reels</div>
        <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)]">Sean's Reels</h2>
        <p className="text-[var(--ink-soft)] mt-1 text-sm">Tap one to watch full-screen, then swipe up for the next.</p>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3 snap-x">
        {reels.map((r, i) => (
          <button
            key={r.id}
            onClick={() => setOpenAt(i)}
            className="relative shrink-0 w-36 sm:w-44 rounded-2xl overflow-hidden snap-start transition-transform hover:-translate-y-1 active:scale-95"
            style={{ aspectRatio: "9 / 16", border: "3px solid var(--ink)", boxShadow: "4px 4px 0 0 var(--ink)", background: "#0e1230" }}
            data-testid={`reel-card-${i}`}
          >
            <video src={`${mediaSrc(r.upload_url)}#t=0.1`} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-11 h-11 rounded-full flex items-center justify-center text-white text-lg" style={{ background: "rgba(255,81,71,0.9)", border: "2px solid #fff" }}>▶</span>
            </span>
            {r.caption && (
              <span className="absolute bottom-0 inset-x-0 p-2 pt-6 text-left text-white text-[0.7rem] font-semibold line-clamp-2" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.8))" }}>
                {r.caption}
              </span>
            )}
          </button>
        ))}
      </div>
      {openAt !== null && <ReelsPlayer reels={reels} start={openAt} onClose={() => setOpenAt(null)} />}
    </section>
  );
}

function ReelsPlayer({ reels, start, onClose }) {
  const scrollRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    el.scrollTop = start * el.clientHeight;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [start, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[70]" style={{ background: "#0b0d1f" }} data-testid="reels-player">
      <div ref={scrollRef} className="h-full w-full overflow-y-scroll snap-y snap-mandatory" style={{ scrollbarWidth: "none" }}>
        {reels.map((r, i) => (
          <ReelSlide key={r.id} reel={r} index={i} muted={muted} setMuted={setMuted} />
        ))}
      </div>
      <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: "rgba(0,0,0,0.5)" }} aria-label="Close reels" data-testid="reels-close-btn">
        <X size={22} />
      </button>
      <button onClick={() => setMuted((m) => !m)} className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: "rgba(0,0,0,0.5)" }} aria-label={muted ? "Unmute" : "Mute"} data-testid="reels-mute-btn">
        {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
      </button>
    </div>,
    document.body
  );
}

function ReelSlide({ reel, index, muted, setMuted }) {
  const ref = useRef(null);
  const videoRef = useRef(null);

  // Play only the reel that's on screen
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      const v = videoRef.current;
      if (!v) return;
      if (e.isIntersecting) {
        v.currentTime = 0;
        v.play().catch(() => { setMuted(true); v.muted = true; v.play().catch(() => {}); });
      } else v.pause();
    }, { threshold: 0.6 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [setMuted]);

  const share = async () => {
    const url = `${window.location.origin}/#sean-reels`;
    if (navigator.share) { try { await navigator.share({ text: reel.caption, url }); } catch {/* cancelled */} }
    else { navigator.clipboard?.writeText(`${reel.caption}\n${url}`); }
  };

  return (
    <div ref={ref} className="relative h-full w-full snap-start flex items-center justify-center" data-testid={`reel-slide-${index}`}>
      <video
        ref={videoRef}
        src={mediaSrc(reel.upload_url)}
        playsInline
        loop
        muted={muted}
        onClick={(e) => (e.target.paused ? e.target.play() : e.target.pause())}
        className="h-full w-full sm:max-w-[460px] object-contain bg-black"
      />
      <div className="absolute bottom-0 inset-x-0 sm:max-w-[460px] sm:mx-auto p-4 pb-8 text-white" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.8))" }}>
        <div className="flex items-center gap-2 mb-1">
          <img src="/sean-avatar.jpg" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
          <span className="font-bold text-sm">seanthesciencekid</span>
        </div>
        {reel.caption && <p className="text-sm leading-snug line-clamp-3">{reel.caption}</p>}
        {reel.tags?.length > 0 && <p className="text-xs opacity-80 mt-1">{reel.tags.map((t) => `#${t}`).join(" ")}</p>}
      </div>
      <button onClick={share} className="absolute right-4 bottom-28 sm:right-[calc(50%-250px)] w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ background: "rgba(0,0,0,0.5)" }} aria-label="Share reel" data-testid={`reel-share-${index}`}>
        <Share2 size={20} />
      </button>
    </div>
  );
}
